<template>
  <div v-for="(address, index) in innerAddresses" :key="address.id">
    <div
      v-if="addressIdToEdit !== address.id"
      :key="address.id"
      class="group mb-4 flex items-center justify-between rounded-sm border p-6 hover:bg-gray-50"
      :data-cy="`address_${index}`"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <icon-base
            :icon="IconHome"
            :icon-color="UTIL_COLORS.DEFAULT"
            class="mr-3"
          />
          <p class="mr-3">
            {{
              isAddressEmpty(address)
                ? $t("COMMON.NO_DATA")
                : formatAddress({
                    address_line: address?.address_line || "",
                    address_line2: address?.address_line2 || "",
                    city: address?.city || "",
                    state: address?.state || "",
                    country: address?.country || "",
                    zip: address?.zip || "",
                  })
            }}
          </p>
        </div>
        <span
          v-if="address.is_primary"
          class="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-500"
        >
          {{ $t("COMMON.PRIMARY_SHORTCUT") }}
        </span>
      </div>
      <div class="flex items-center opacity-0 group-hover:opacity-100">
        <icon-base
          :role="`editBtn_${index}`"
          :icon="IconEdit"
          class="mr-3 cursor-pointer"
          @click="editAddress(address.id || null)"
        />
        <icon-base
          v-if="!address.is_primary && canDelete"
          :icon="IconDelete"
          class="cursor-pointer text-disabled-medium"
          @click="deleteAddress(address)"
        />
      </div>
    </div>

    <address-fields
      v-if="addressIdToEdit === address.id"
      :key="address.id"
      v-model:address="innerAddresses[index]"
      @close="handleClose"
      @update-primary-value="handlePrimaryChange"
    />
  </div>
  <button
    class="mb-6 flex w-full items-center justify-center rounded-sm bg-blue-50 p-5 text-xs font-medium text-blue-500 hover:bg-blue-100"
    @click.prevent="addNewAddress"
  >
    + {{ $t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.ADD_NEW_ADDRESS") }}
  </button>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { deepEqual } from "fast-equals";
import cloneDeep from "lodash/cloneDeep";

import { UTIL_COLORS } from "@/helpers/constants";
import { formatAddress } from "@/helpers/formatting";

import IconDelete from "@/components/icons/IconDelete.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import IconHome from "@/components/icons/IconHome.vue";
import AddressFields from "@/views/deals/components/AddressFields.vue";

import type { IAddress } from "@/models/common";
import type { DeleteRecord } from "@/models/workflows";
import type { PropType } from "vue";

const props = defineProps({
  addresses: {
    type: Array as PropType<(IAddress | DeleteRecord)[]>,
    required: true,
  },
  canDelete: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  (eventName: "update:addresses", adresses: (DeleteRecord | IAddress)[]): void;
}>();

const addressIdToEdit = ref<number | string | null>(null);
const innerAddresses = ref<IAddress[]>(
  cloneDeep(
    props.addresses.filter((item) => !("_delete" in item)) as IAddress[]
  )
);
const addressesToDelete = ref<DeleteRecord[]>([]);

const addressToEdit = computed(() =>
  innerAddresses.value.find((address) => address.id === addressIdToEdit.value)
);

const handleClose = () => {
  addressIdToEdit.value = null;
};

const addNewAddress = () => {
  const newAddressId = Number(Math.random().toString().split(".").join(""));
  innerAddresses.value.push({
    id: newAddressId,
    address_line: "",
    address_line2: "",
    city: "",
    country: "",
    state: "",
    zip: "",
    is_primary: props.addresses.length === 0 ? true : false,
  });
  addressIdToEdit.value = newAddressId;
};

const handlePrimaryChange = () => {
  innerAddresses.value.forEach((addrs) => {
    if (addrs.id !== addressToEdit.value?.id) {
      addrs.is_primary = false;
    }
  });
};

const editAddress = (id: number | null) => {
  addressIdToEdit.value = id;
};

const deleteAddress = (address: IAddress) => {
  innerAddresses.value.splice(innerAddresses.value.indexOf(address), 1);
  if (address.id) {
    addressesToDelete.value.push({ id: address.id, _delete: true });
  }
};

const isAddressEmpty = (address: IAddress): boolean =>
  !address?.address_line &&
  !address?.address_line2 &&
  !address?.city &&
  !address?.country &&
  !address?.state &&
  !address?.zip;

watch(
  innerAddresses,
  () =>
    emit("update:addresses", [
      ...innerAddresses.value,
      ...addressesToDelete.value,
    ]),
  {
    deep: true,
  }
);

watch(
  () => props.addresses,
  (newVal) => {
    if (
      deepEqual(newVal, [...innerAddresses.value, ...addressesToDelete.value])
    ) {
      return;
    }
    innerAddresses.value = props.addresses.filter(
      (item) => !("_delete" in item)
    ) as IAddress[];
  },
  {
    deep: true,
  }
);
</script>
